import {
  ClipboardDocumentIcon,
  PencilSquareIcon,
  TrashIcon,
} from "@heroicons/react/24/outline";
import Button from "../../../componentLibrary/Button";
import Card from "../../../componentLibrary/Card";
import { useNotification } from "../../../hooks/useNotification";
import { ImageI, ImageTypeEnum } from "../../../types";
import { onCopyClipboard } from "../../CreateLessonPage/utils/onCopyClipboard";

type ImageShowcaseProps = {
  image: ImageI;
  handleEditClick: (image: ImageI) => void;
  handleDeleteClick: (image: ImageI) => void;
  canUpdateContent?: boolean;
  canDeleteContent?: boolean;
};

const ImageShowcase = ({
  image,
  handleEditClick,
  handleDeleteClick,
  canUpdateContent = true,
  canDeleteContent = true,
}: ImageShowcaseProps) => {
  const { notify } = useNotification();

  return (
    <Card
      key={image._id}
      className="group hover:shadow-lg transition-shadow h-full"
    >
      <div className="flex flex-col h-full">
        <div className="aspect-square bg-gray-50 rounded-lg mb-3 overflow-hidden">
          <img
            src={image.src}
            alt={image.alt}
            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-200"
            onError={(e) => {
              const target = e.target as HTMLImageElement;
              target.src = "/api/placeholder/200/200?text=Image+Not+Found";
            }}
          />
        </div>

        <div className="flex flex-col flex-grow space-y-2">
          <div className="flex items-center justify-between">
            <h3
              className="font-semibold text-gray-900 truncate"
              title={image.name}
            >
              {image.name}
            </h3>
            <span
              className={`px-2 py-1 text-xs font-medium rounded-full ${
                image.type === ImageTypeEnum.ICON
                  ? "bg-blue-100 text-blue-800"
                  : "bg-green-100 text-green-800"
              }`}
            >
              {image.type}
            </span>
          </div>

          <p
            className="text-sm text-gray-600 line-clamp-2 flex-grow"
            title={image.alt}
          >
            {image.alt}
          </p>

          <div className="flex gap-2 pt-2 mt-auto">
            <Button.Square
              variant="secondary"
              className="flex-1"
              onClick={() => {
                onCopyClipboard(image.src);
                notify("success", "Image URL copied to clipboard");
              }}
              title="Copy Image URL"
            >
              <ClipboardDocumentIcon className="h-4 w-4" />
            </Button.Square>
            <Button.Square
              variant="secondary"
              className="flex-1"
              onClick={() => canUpdateContent && handleEditClick(image)}
              disabled={!canUpdateContent}
              title={
                !canUpdateContent
                  ? "Ask an admin if you need permission to edit images"
                  : "Edit image"
              }
            >
              <PencilSquareIcon className="h-4 w-4" />
            </Button.Square>
            <Button.Square
              variant="cautionary"
              className="flex-1"
              onClick={() => canDeleteContent && handleDeleteClick(image)}
              disabled={!canDeleteContent}
              title={
                !canDeleteContent
                  ? "Ask an admin if you need permission to delete images"
                  : "Delete image"
              }
            >
              <TrashIcon className="h-4 w-4" />
            </Button.Square>
          </div>
        </div>
      </div>
    </Card>
  );
};

export default ImageShowcase;
